import { jsx as _jsx } from "react/jsx-runtime";
/**
 * What one viewport's world gathers from the nodes inside it, as Godot's World3D keeps it. A
 * publication set holds every node's value in tree order. A group is a set of WorldEnvironment
 * resolutions whose chooser answers the first one in tree order that holds a valid resource
 * (`world_environment.cpp:43-48`, `:87-93`). Godot keeps a group per resource slot, so the
 * Environment and the CameraAttributes each choose their node apart (ADR-0025).
 */
import { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { compareTreeOrder, useTreeOrder } from '../contexts/TreeOrderContext';
/** The one `pending` and `none` value of every slot, so an unchanged resolution publishes nothing. */
export const PENDING_RESOLUTION = { status: 'pending' };
export const NO_RESOLUTION = { status: 'none' };
/**
 * The publications sort in tree order, as Godot's group sorts its nodes, an ancestor first
 * (`scene_tree.cpp:1568-1581`). React's mount order differs for a late sub-scene and for a child.
 *
 * @canonical A value a node publishes to its viewport's world
 */
export function createWorldPublications() {
    const PublisherContext = createContext(null);
    const PublicationsContext = createContext([]);
    function Provider({ children }) {
        const [publications, setPublications] = useState([]);
        const publisher = useMemo(() => ({
            publish: (publication) => setPublications((current) => {
                const previous = current.find((entry) => entry.key === publication.key);
                if (previous && isSamePublication(previous, publication))
                    return current;
                return previous
                    ? current.map((entry) => (entry === previous ? publication : entry))
                    : [...current, publication];
            }),
            withdraw: (key) => setPublications((current) => current.filter((entry) => entry.key !== key)),
        }), []);
        return (_jsx(PublisherContext.Provider, { value: publisher, children: _jsx(PublicationsContext.Provider, { value: publications, children: children }) }));
    }
    function usePublish(value) {
        const publisher = useContext(PublisherContext);
        if (!publisher)
            throw new Error('expected a world environment scope above the publishing node, found none');
        const [key] = useState(() => ({}));
        const order = useTreeOrder();
        useLayoutEffect(() => () => publisher.withdraw(key), [publisher, key]);
        useLayoutEffect(() => publisher.publish({ key, order, value }), [publisher, key, order, value]);
    }
    function useAll() {
        const publications = useContext(PublicationsContext);
        // Sorted here, once for a commit that publishes many, not on each publication.
        return useMemo(() => [...publications].sort((a, b) => compareTreeOrder(a.order, b.order)).map((entry) => entry.value), [publications]);
    }
    return { Provider, usePublish, useAll };
}
function isSamePublication(a, b) {
    return a.value === b.value && compareTreeOrder(a.order, b.order) === 0;
}
/**
 * A `pending` publication holds its place, since its resource may turn out valid.
 *
 * @canonical The first valid resource of a world's WorldEnvironments
 */
export function createWorldGroup() {
    const { Provider, usePublish, useAll } = createWorldPublications();
    function useChosen() {
        const resolutions = useAll();
        return useMemo(() => {
            if (resolutions.length === 0)
                return null;
            return resolutions.find((resolution) => resolution.status !== 'none') ?? NO_RESOLUTION;
        }, [resolutions]);
    }
    return { Provider, usePublish, useChosen };
}
//# sourceMappingURL=worldGroup.js.map